import type { DogFormData, Recipe } from '../types';
import type { PetSnapshot } from '../types/pets';

export type { PetSnapshot } from '../types/pets';

export interface MultiPetCartLine {
  /** Canonical product.id (UUID) — key of the checkout distribution. */
  productId: string;
  quantity: number;
}

export interface MultiPetCartAttributes {
  num_mascotas: string;
  mascotas_json: string;
}

export interface MultiPetCartPayload {
  lines: MultiPetCartLine[];
  attributes: MultiPetCartAttributes;
}

export interface MultiPetCartInputPet {
  nombre: string;
  formData: Partial<DogFormData>;
}

interface MascotaMetaEntry {
  nombre: string;
  // Variantes REALES de Shopify de este perro (dato de soporte del carrito de
  // Shopify): mascotas_json NO usa el UUID del funnel sino el variant id real.
  recetas: string[];
  num_variantes: number;
  // Raciones del PRIMER pedido por variante de ESTE perro (14 días si pack de
  // transición; mensuales si pedido completo — sale de
  // initiationPackDistribution). El webhook las CONVIERTE a raciones/mes y las
  // persiste como extra_data.variant_quantities en su Pet: sin esto, al
  // editar el plan de un perro en Mi Cuenta no se sabe qué porción de una
  // receta COMPARTIDA con otro perro es suya y se pisa la del otro. Keyed por
  // el variant id real de Shopify.
  cantidades: Record<string, number>;
}

/** Build a productId (UUID) → real Shopify variant id lookup from the catalog.
 *  Covers both the recipe's own productId and each size variant's productId. */
function buildProductIdToVariantId(recipes: ReadonlyArray<Recipe>): Record<string, string> {
  const map: Record<string, string> = {};
  for (const recipe of recipes) {
    if (recipe.productId && recipe.shopifyVariantId) map[recipe.productId] = recipe.shopifyVariantId;
    for (const variant of Object.values(recipe.variants || {})) {
      if (variant.productId && variant.shopifyVariantId) map[variant.productId] = variant.shopifyVariantId;
    }
  }
  return map;
}

/**
 * Build the multi-pet cart payload.
 *
 * `lines` aggregate the recipe distribution keyed by canonical product.id (UUID)
 * — the checkout `initiation_pack_distribution` key. `mascotas_json` (Shopify
 * support attribute) instead uses the REAL Shopify variant ids, resolved from
 * the catalog via `recipes`. When a productId has no known variant (or `recipes`
 * is omitted) the productId is used verbatim as a fallback.
 */
export function buildMultiPetCartPayload(
  pets: ReadonlyArray<MultiPetCartInputPet>,
  recipes: ReadonlyArray<Recipe> = [],
): MultiPetCartPayload {
  if (pets.length === 0) {
    throw new Error('buildMultiPetCartPayload: pets array is empty');
  }

  const productIdToVariantId = buildProductIdToVariantId(recipes);
  const toVariantId = (productId: string): string => productIdToVariantId[productId] || productId;

  const aggregated = new Map<string, number>();
  const mascotasMeta: MascotaMetaEntry[] = [];
  const petsMissingDistribution: string[] = [];

  for (const pet of pets) {
    const dist = pet.formData.initiationPackDistribution;
    if (!dist || Object.keys(dist).length === 0) {
      petsMissingDistribution.push(pet.nombre || '(sin nombre)');
      continue;
    }

    const variantIds: string[] = [];
    const cantidades: Record<string, number> = {};
    for (const [productId, qty] of Object.entries(dist)) {
      if (typeof qty !== 'number' || !Number.isFinite(qty) || qty <= 0) {
        throw new Error(
          `buildMultiPetCartPayload: invalid quantity for variant ${productId} on pet "${pet.nombre}": ${String(qty)}`,
        );
      }
      const prev = aggregated.get(productId) ?? 0;
      aggregated.set(productId, prev + qty);
      // mascotas_json es soporte Shopify: guarda el variant id real.
      const variantId = toVariantId(productId);
      variantIds.push(variantId);
      cantidades[variantId] = (cantidades[variantId] ?? 0) + qty;
    }

    mascotasMeta.push({
      nombre: pet.nombre,
      recetas: variantIds,
      num_variantes: variantIds.length,
      cantidades,
    });
  }

  if (petsMissingDistribution.length > 0) {
    throw new Error(
      `buildMultiPetCartPayload: ${petsMissingDistribution.length}/${pets.length} mascota(s) sin initiationPackDistribution: ${petsMissingDistribution.join(', ')}. ` +
        'No se puede construir el carrito sin la distribución de cada mascota.',
    );
  }

  if (mascotasMeta.length !== pets.length) {
    throw new Error(
      `buildMultiPetCartPayload: expected ${pets.length} mascotas en payload, got ${mascotasMeta.length}`,
    );
  }

  const lines: MultiPetCartLine[] = Array.from(aggregated.entries())
    .filter(([, qty]) => qty > 0)
    .map(([productId, quantity]) => ({ productId, quantity }));

  if (lines.length === 0) {
    throw new Error('buildMultiPetCartPayload: aggregated lines are empty');
  }

  return {
    lines,
    attributes: {
      num_mascotas: String(pets.length),
      mascotas_json: JSON.stringify(mascotasMeta),
    },
  };
}

export function snapshotsToPets(
  petsCompleted: ReadonlyArray<PetSnapshot>,
  currentFormData: Partial<DogFormData>,
): MultiPetCartInputPet[] {
  const result: MultiPetCartInputPet[] = [];
  const droppedSnapshots: string[] = [];

  for (const snapshot of petsCompleted) {
    const dist = snapshot.formData.initiationPackDistribution;
    if (!dist || Object.keys(dist).length === 0) {
      droppedSnapshots.push(snapshot.nombre || snapshot.id || '(sin nombre)');
      continue;
    }
    result.push({ nombre: snapshot.nombre, formData: snapshot.formData });
  }

  if (droppedSnapshots.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[snapshotsToPets] Filtradas ${droppedSnapshots.length} mascota(s) sin initiationPackDistribution: ${droppedSnapshots.join(', ')}`,
    );
  }

  result.push({
    nombre: currentFormData.nombre ?? 'Mi perro',
    formData: currentFormData,
  });

  return result;
}

export function sumMultiPetPricing(
  petsCompleted: ReadonlyArray<PetSnapshot>,
  currentInitiation: number,
  currentMonthly: number,
): { totalInitiation: number; totalMonthly: number } {
  let totalInitiation = currentInitiation;
  let totalMonthly = currentMonthly;

  for (const pet of petsCompleted) {
    // Mismo fallback que el desglose visual de CartSummary (monthlyPetLines):
    // si pricing está undefined pero formData trae el precio, usarlo — antes
    // la cabecera contaba 0 y la línea mostraba el precio, así que la suma de
    // líneas no cuadraba con el total (auditoría 2026-06-11).
    totalInitiation += pet.pricing?.initiationPrice ?? pet.formData?.initiationPackPrice ?? 0;
    totalMonthly += pet.pricing?.monthlyDiscountedPrice ?? pet.formData?.monthlyPlanPrice ?? 0;
  }

  return { totalInitiation, totalMonthly };
}
